import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import './PermitForm.css';
import { API_BASE } from '../config/apiConfig';

const IndyResidentialWreckingForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [formData, setFormData] = useState({
    permitType: 'indy_res_wrecking',
    // Property owner / applicant
    ownerName: '',
    applicantName: '',
    email: '',
    phone: '',

    // Property
    streetAddress: '',
    addressLine2: '',
    city: 'Indianapolis',
    state: 'IN',
    zipCode: '',

    // Structure to be removed
    structureType: '',
    isPrimaryResidence: '',
    scopeOfWork: '',

    notes: ''
  });

  useEffect(() => {
    const saveDraft = () => {
      const draftData = {
        formData,
        attachments: attachments.map(file => ({ name: file.name })),
        timestamp: new Date().toISOString()
      };
      localStorage.setItem('indyResWreckingDraft', JSON.stringify(draftData));
    };
    const timeoutId = setTimeout(saveDraft, 500);
    return () => clearTimeout(timeoutId);
  }, [formData, attachments]);

  useEffect(() => {
    if (!location.state?.prefillData) {
      const savedDraft = localStorage.getItem('indyResWreckingDraft');
      if (savedDraft) {
        try {
          const draft = JSON.parse(savedDraft);
          const draftDate = new Date(draft.timestamp);
          const daysSinceDraft = (new Date() - draftDate) / (1000 * 60 * 60 * 24);
          if (daysSinceDraft < 7) {
            setFormData(draft.formData);
          } else {
            localStorage.removeItem('indyResWreckingDraft');
          }
        } catch (err) {
          console.error('Failed to load Indy wrecking draft:', err);
        }
      }
    } else {
      setFormData(prev => ({
        ...prev,
        ...location.state.prefillData,
        permitType: 'indy_res_wrecking'
      }));
      if (location.state.rejectionReason) {
        alert(`Previous application was rejected: ${location.state.rejectionReason}\n\nPlease review and correct your information before resubmitting.`);
      }
      localStorage.removeItem('indyResWreckingDraft');
    }
  }, [location.state]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    setAttachments(files);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const multipartData = new FormData();

      const selectedLocationRaw = localStorage.getItem('selectedCounty');
      let submissionFields = { ...formData };
      if (selectedLocationRaw) {
        try {
          const loc = JSON.parse(selectedLocationRaw);
          submissionFields = {
            ...submissionFields,
            jurisdictionCity: loc.city || '',
            jurisdictionCounty: loc.county || '',
            jurisdictionState: loc.state || '',
            jurisdictionStateAbbr: loc.stateAbbr || ''
          };
        } catch (err) {
          console.error('Failed to parse selectedCounty for wrecking:', err);
        }
      }

      Object.entries(submissionFields).forEach(([key, value]) => {
        multipartData.append(key, value ?? '');
      });

      attachments.forEach(file => {
        multipartData.append('attachments', file);
      });

      const response = await axios.post(`${API_BASE}/submit`, multipartData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        localStorage.removeItem('indyResWreckingDraft');
        navigate(`/status/${response.data.submissionId}`);
      }
    } catch (error) {
      alert('Failed to submit application: ' + (error.response?.data?.error || error.message));
      setLoading(false);
    }
  };

  return (
    <div className="form-container">
      <div className="form-card">
        <h2>Indianapolis – Residential Wrecking / Removal Permit</h2>

        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h3>Property Information</h3>
            <div className="form-grid">
              <div className="form-group full-width">
                <label>Property Address *</label>
                <input
                  type="text"
                  name="streetAddress"
                  value={formData.streetAddress}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Address Line 2</label>
                <input
                  type="text"
                  name="addressLine2"
                  value={formData.addressLine2}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>City *</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>State *</label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>ZIP Code *</label>
                <input
                  type="text"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Owner / Applicant</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Property Owner Name *</label>
                <input
                  type="text"
                  name="ownerName"
                  value={formData.ownerName}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Applicant Name *</label>
                <input
                  type="text"
                  name="applicantName"
                  value={formData.applicantName}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Phone *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Structure / Wrecking Details</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Type of Structure</label>
                <input
                  type="text"
                  name="structureType"
                  value={formData.structureType}
                  onChange={handleChange}
                  placeholder="e.g. Single-family dwelling, Garage, Shed"
                />
              </div>
              <div className="form-group">
                <label>Is this the primary residence?</label>
                <select
                  name="isPrimaryResidence"
                  value={formData.isPrimaryResidence}
                  onChange={handleChange}
                >
                  <option value="">Select</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
              <div className="form-group full-width">
                <label>Scope of Work *</label>
                <textarea
                  name="scopeOfWork"
                  value={formData.scopeOfWork}
                  onChange={handleChange}
                  rows={3}
                  required
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Application &amp; Supporting Documents</h3>
            <p className="form-note">
              For this demo, upload the completed Indianapolis Wrecking or Removal Permit Application PDF
              and any supporting documentation.
            </p>
            <div className="form-group">
              <label>Upload PDF(s) *</label>
              <input
                type="file"
                multiple
                accept=".pdf"
                onChange={handleFileChange}
                required={attachments.length === 0}
              />
              {attachments.length > 0 && (
                <p className="form-note-small">{attachments.length} file(s) selected</p>
              )}
            </div>
          </div>

          <div className="form-section">
            <h3>Additional Notes</h3>
            <div className="form-group">
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={3}
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="submit-button" disabled={loading}>
              {loading ? 'Submitting...' : 'Submit Application'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default IndyResidentialWreckingForm;

